import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import env from 'config/env';
import { useAuth } from './AuthContext';

interface SubscriptionStatus {
  isSubscribed: boolean;
  monthlyMessageCount: number;
  messageCountResetDate: string | null;
  currentPeriodEnd: string | null;
  plan: string | null;
}

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  updateSubscriptionStatus: () => Promise<void>;
  updateMessageCount: (newCount: number) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const { user } = useAuth();

  const updateSubscriptionStatus = async () => {
    if (!user) {
      setSubscription(null);
      return;
    }
    try {
      const response = await axios.get(`${env.API_URL}/subscription/status`);
      setSubscription({
        isSubscribed: response.data.isSubscribed,
        monthlyMessageCount: response.data.monthlyMessageCount || 0,
        messageCountResetDate: response.data.messageCountResetDate,
        currentPeriodEnd: response.data.currentPeriodEnd,
        plan: response.data.plan
      });
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const updateMessageCount = (newCount: number) => {
    if (subscription) {
      setSubscription({
        ...subscription,
        monthlyMessageCount: newCount
      });
    }
  };

  // Check subscription status periodically to handle resets
  useEffect(() => {
    if (user) {
      // Initial check
      updateSubscriptionStatus();

      // Check every minute for message count resets
      const interval = setInterval(() => {
        if (subscription?.messageCountResetDate) {
          const resetDate = new Date(subscription.messageCountResetDate);
          if (new Date() > resetDate) {
            updateSubscriptionStatus();
          }
        }
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [user, subscription?.messageCountResetDate]);

  return (
    <SubscriptionContext.Provider value={{ 
      subscription, 
      updateSubscriptionStatus,
      updateMessageCount
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 
